import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { adsApi } from '@/api/client';
import { getDefaultDashboardDateRange } from '@/api/reportDateRanges';
import { campaignAdProductToReportId } from '@/api/reportAdProducts';
import type { CampaignAdProduct } from '@/types/spCampaigns';
import { EditableKeywordBidCell } from '@/components/EditableKeywordBidCell';
import { TableSearchInput } from '@/components/TableSearchInput';
import { filterRowsByTableSearch } from '@/utils/tableSearch';
import { resolveEditableKeywordBid } from '@/api/keywordBidUpdate';
import type { KeywordAdProduct } from '@/api/unifiedKeywords';
import {
  buildKeywordMetricsMap,
  formatCurrencyValue,
  formatNumberValue,
  formatPercentValue,
  keywordMetricsForCatalogRow,
  parseKeywordsPayload,
  parseReportRowsPayload,
  type SpKeywordRow,
} from '@/api/keywordMetrics';

type KeywordStateFilter = 'ALL' | 'ENABLED' | 'PAUSED' | 'ARCHIVED';

type CampaignKeywordsSectionProps = {
  profileId: string;
  campaignId: string;
  adProduct: CampaignAdProduct;
  adGroupNameById?: Map<string, string>;
  /** Scope the keyword list to a single ad group. */
  adGroupId?: string;
  /** Render without the outer card (when nested inside another panel). */
  embedded?: boolean;
};

function campaignToKeywordAdProduct(adProduct: CampaignAdProduct): KeywordAdProduct | null {
  if (adProduct === 'SP' || adProduct === 'SB') return adProduct;
  return null;
}

function formatBid(bid: number | undefined): string {
  if (bid == null) return '-';
  return formatCurrencyValue(bid);
}

export function formatDateRangeLabel(startDate: string, endDate: string): string {
  const fmt = (iso: string) => {
    const d = new Date(`${iso}T12:00:00`);
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };
  return `${fmt(startDate)} – ${fmt(endDate)}`;
}

export function CampaignKeywordsSection({
  profileId,
  campaignId,
  adProduct,
  adGroupNameById,
  adGroupId,
  embedded = false,
}: CampaignKeywordsSectionProps) {
  const keywordAdProduct = campaignToKeywordAdProduct(adProduct);
  const dateRange = useMemo(() => getDefaultDashboardDateRange(), []);
  const [stateFilter, setStateFilter] = useState<KeywordStateFilter>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [spNextToken, setSpNextToken] = useState<string | null>(null);
  const [accumulatedKeywords, setAccumulatedKeywords] = useState<SpKeywordRow[]>([]);

  const statesParam =
    stateFilter === 'ALL' ? (['ENABLED', 'PAUSED', 'ARCHIVED'] as const) : ([stateFilter] as const);

  const {
    data: catalogData,
    isLoading: isCatalogLoading,
    error: catalogError,
    refetch: refetchCatalog,
    isFetching: isCatalogFetching,
  } = useQuery({
    queryKey: [
      'campaign-keywords-catalog',
      profileId,
      campaignId,
      adProduct,
      stateFilter,
      spNextToken,
      adGroupId ?? null,
    ],
    queryFn: async () => {
      if (keywordAdProduct === 'SP') {
        const res = await adsApi.listSpKeywords(profileId, {
          include_extended_data_fields: true,
          campaign_ids: [campaignId],
          ...(adGroupId ? { ad_group_ids: [adGroupId] } : {}),
          states: [...statesParam],
          max_results: 1000,
          next_token: spNextToken,
        });
        if (!res.success) throw new Error('Failed to load keywords');
        return res.data;
      }
      if (keywordAdProduct === 'SB') {
        const res = await adsApi.getSbKeywords(profileId, {
          campaignId,
          count: 500,
        });
        if (!res.success) throw new Error('Failed to load keywords');
        return res.data;
      }
      return { keywords: [], total_results: 0, next_token: null };
    },
    enabled: !!profileId && !!campaignId && keywordAdProduct !== null,
  });

  const catalogPayload = useMemo(() => parseKeywordsPayload(catalogData), [catalogData]);

  useEffect(() => {
    setSpNextToken(null);
    setAccumulatedKeywords([]);
  }, [profileId, campaignId, stateFilter, adGroupId]);

  useEffect(() => {
    if (keywordAdProduct !== 'SP' || !catalogData || isCatalogFetching) return;
    const payload = parseKeywordsPayload(catalogData);
    if (!spNextToken) {
      setAccumulatedKeywords(payload.keywords);
      return;
    }
    setAccumulatedKeywords((prev) => [...prev, ...payload.keywords]);
  }, [catalogData, keywordAdProduct, spNextToken, isCatalogFetching]);

  const catalogKeywords = useMemo(() => {
    if (keywordAdProduct === 'SP') {
      return accumulatedKeywords.length > 0 ? accumulatedKeywords : catalogPayload.keywords;
    }
    // SB keywords are listed per campaign, so ad-group scoping happens here.
    const kws = adGroupId
      ? catalogPayload.keywords.filter((k) => k.ad_group_id === adGroupId)
      : catalogPayload.keywords;
    if (stateFilter === 'ALL') return kws;
    return kws.filter((k) => (k.state || '').toUpperCase() === stateFilter);
  }, [keywordAdProduct, accumulatedKeywords, catalogPayload.keywords, stateFilter, adGroupId]);

  const {
    data: metricsData,
    isLoading: isMetricsLoading,
    error: metricsError,
    refetch: refetchMetrics,
  } = useQuery({
    queryKey: [
      'campaign-keyword-metrics',
      profileId,
      campaignId,
      adProduct,
      dateRange.startDate,
      dateRange.endDate,
    ],
    queryFn: async () => {
      const res = await adsApi.getKeywordReportRows(profileId, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        adProducts: campaignAdProductToReportId(adProduct),
        campaignId,
      });
      if (!res.success) throw new Error('Failed to load keyword metrics');
      return res.data;
    },
    enabled: !!profileId && !!campaignId && keywordAdProduct !== null,
  });

  const metricsByKeyword = useMemo(() => {
    const parsed = parseReportRowsPayload(metricsData);
    return buildKeywordMetricsMap(parsed.rows);
  }, [metricsData]);

  const sortedRows = useMemo(() => {
    const rows = catalogKeywords.map((keyword) => {
      const metrics = keywordMetricsForCatalogRow(
        keyword,
        keywordAdProduct!,
        metricsByKeyword
      );
      return { keyword, metrics };
    });
    return rows.sort((a, b) => {
      const spendA = a.metrics.totalCost ?? -1;
      const spendB = b.metrics.totalCost ?? -1;
      if (spendB !== spendA) return spendB - spendA;
      return (a.keyword.keyword_text || '').localeCompare(b.keyword.keyword_text || '');
    });
  }, [catalogKeywords, keywordAdProduct, metricsByKeyword]);

  const visibleRows = useMemo(() => {
    return filterRowsByTableSearch(sortedRows, searchQuery, ({ keyword, metrics }) => {
      const adGroupId = keyword.ad_group_id || '';
      const adGroupName =
        (adGroupId && adGroupNameById?.get(adGroupId)) || adGroupId || '-';
      return [
        keyword.keyword_text,
        adGroupName,
        keyword.match_type,
        formatBid(keyword.bid),
        keyword.state,
        keyword.extended_data?.serving_status,
        keyword.keyword_id,
        formatNumberValue(metrics.impressions, 0),
        formatNumberValue(metrics.clicks, 0),
        formatCurrencyValue(metrics.totalCost),
        formatCurrencyValue(metrics.cpc),
        formatPercentValue(metrics.ctr),
        formatNumberValue(metrics.purchases, 0),
        formatCurrencyValue(metrics.sales),
        formatNumberValue(metrics.roas),
      ];
    });
  }, [sortedRows, searchQuery, adGroupNameById]);

  const handleLoadMore = () => {
    if (!catalogPayload.nextToken) return;
    setSpNextToken(catalogPayload.nextToken);
  };

  const handleStateFilterChange = (value: KeywordStateFilter) => {
    setStateFilter(value);
  };

  const handleRefresh = () => {
    setSpNextToken(null);
    setAccumulatedKeywords([]);
    void refetchCatalog();
    void refetchMetrics();
  };

  if (!keywordAdProduct) {
    return null;
  }

  const isLoading = isCatalogLoading || isCatalogFetching;
  const hasCatalogError = !!catalogError;
  const performanceLabel = formatDateRangeLabel(dateRange.startDate, dateRange.endDate);

  return (
    <div className={embedded ? undefined : 'bg-surface rounded-xl shadow border border-border p-6'}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Keywords</h2>
          <p className="text-sm text-foreground-subtle mt-0.5">Performance: {performanceLabel}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <TableSearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search keywords…"
            id="campaign-keywords-search"
          />
          <div>
            <label className="block text-xs font-medium text-foreground-muted uppercase tracking-wide mb-1">
              State
            </label>
            <select
              value={stateFilter}
              onChange={(e) => handleStateFilterChange(e.target.value as KeywordStateFilter)}
              className="rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
            >
              <option value="ALL">All</option>
              <option value="ENABLED">Enabled</option>
              <option value="PAUSED">Paused</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            className="text-sm px-3 py-2 border border-border rounded-lg hover:bg-page"
          >
            Refresh
          </button>
        </div>
      </div>

      {isLoading && sortedRows.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-foreground-subtle">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading keywords...
        </div>
      ) : hasCatalogError ? (
        <div className="text-sm text-error">
          {(catalogError as Error).message || 'Failed to load keywords'}
          <button type="button" onClick={handleRefresh} className="ml-2 text-brand underline">
            Retry
          </button>
        </div>
      ) : sortedRows.length === 0 ? (
        <p className="text-sm text-foreground-subtle">No keywords found for this {adGroupId ? 'ad group' : 'campaign'}.</p>
      ) : visibleRows.length === 0 ? (
        <p className="text-sm text-foreground-subtle">No rows match your search.</p>
      ) : (
        <>
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-sm text-foreground-subtle">
              {searchQuery.trim()
                ? `Showing ${visibleRows.length} of ${sortedRows.length} keyword(s)`
                : `${sortedRows.length} keyword(s)`}
            </span>
            {metricsError ? (
              <span className="text-xs text-warning">
                Metrics unavailable — sync keyword reports for performance data.
              </span>
            ) : isMetricsLoading ? (
              <span className="text-xs text-chrome-label">Loading metrics…</span>
            ) : null}
          </div>
          <div className="max-h-96 overflow-x-auto overflow-y-auto rounded-xl border border-border">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-page text-left text-xs font-medium text-foreground-muted uppercase tracking-wide">
                  <th className="px-3 py-2">Keyword</th>
                  <th className="px-3 py-2">Ad group</th>
                  <th className="px-3 py-2">Match</th>
                  <th className="px-3 py-2 text-right">Bid</th>
                  <th className="px-3 py-2">State</th>
                  <th className="px-3 py-2">Serving</th>
                  <th className="px-3 py-2 font-mono">Keyword ID</th>
                  <th className="px-3 py-2 text-right">Impressions</th>
                  <th className="px-3 py-2 text-right">Clicks</th>
                  <th className="px-3 py-2 text-right">Spend</th>
                  <th className="px-3 py-2 text-right">CPC</th>
                  <th className="px-3 py-2 text-right">CTR</th>
                  <th className="px-3 py-2 text-right">Purchases</th>
                  <th className="px-3 py-2 text-right">Sales</th>
                  <th className="px-3 py-2 text-right">ROAS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {visibleRows.map((row, index) => {
                  const { keyword, metrics } = row;
                  const keywordId = keyword.keyword_id || `idx-${index}`;
                  const adGroupId = keyword.ad_group_id || '';
                  const adGroupName =
                    (adGroupId && adGroupNameById?.get(adGroupId)) || adGroupId || '-';
                  const servingStatus = keyword.extended_data?.serving_status || '-';
                  const metricsLoading = isMetricsLoading;

                  return (
                    <tr key={keywordId} className="bg-surface hover:bg-page/80">
                      <td className="px-3 py-2 text-foreground font-medium">
                        {keyword.keyword_text || '-'}
                      </td>
                      <td className="px-3 py-2 text-foreground-muted" title={adGroupId}>
                        {adGroupName}
                      </td>
                      <td className="px-3 py-2 text-foreground-muted uppercase text-xs">
                        {keyword.match_type || '-'}
                      </td>
                      <td className="px-3 py-2 text-foreground-muted text-right font-mono">
                        <EditableKeywordBidCell
                          profileId={profileId}
                          displayValue={formatBid(keyword.bid)}
                          editableTarget={
                            keywordAdProduct
                              ? resolveEditableKeywordBid(keyword, keywordAdProduct)
                              : null
                          }
                          keywordLabel={keyword.keyword_text || undefined}
                          onUpdated={() => {
                            void refetchCatalog();
                          }}
                        />
                      </td>
                      <td className="px-3 py-2 text-foreground-muted">{keyword.state || '-'}</td>
                      <td className="px-3 py-2 text-foreground-muted">{servingStatus}</td>
                      <td className="px-3 py-2 text-foreground-muted font-mono text-xs">
                        {keyword.keyword_id || '-'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {metricsLoading ? '…' : formatNumberValue(metrics.impressions, 0)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {metricsLoading ? '…' : formatNumberValue(metrics.clicks, 0)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {metricsLoading ? '…' : formatCurrencyValue(metrics.totalCost)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {metricsLoading ? '…' : formatCurrencyValue(metrics.cpc)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {metricsLoading ? '…' : formatPercentValue(metrics.ctr)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {metricsLoading ? '…' : formatNumberValue(metrics.purchases, 0)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {metricsLoading ? '…' : formatCurrencyValue(metrics.sales)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {metricsLoading ? '…' : formatNumberValue(metrics.roas)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {searchQuery.trim() && catalogPayload.nextToken ? (
            <p className="text-xs text-foreground-subtle mt-2">
              Search applies to loaded rows only — load more to include additional results.
            </p>
          ) : null}
          {keywordAdProduct === 'SP' && catalogPayload.nextToken ? (
            <div className="mt-3">
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={isCatalogFetching}
                className="text-sm px-3 py-2 border border-border rounded-lg hover:bg-page disabled:opacity-50"
              >
                {isCatalogFetching ? 'Loading…' : 'Load more'}
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
